import React, { useState } from "react";
import PropTypes from "prop-types";

import { Checkbox, Form, Modal, Button, Typography, Divider } from "antd";

import LOCALIZATION from "@Vytelle/admin/src/services/LocalizationService";

const { Text, Title, Paragraph } = Typography;

function TermsCheckbox({ name, ...rest }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const form = Form.useFormInstance();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleModalAgree = () => {
    setIsModalVisible(false);
    form.setFieldValue(name, true);
    form.validateFields([name]);
  };

  return (
    <>
      <Form.Item
        name={name}
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.reject(
                  new Error(
                    LOCALIZATION.USER_SIGN_UP.TERMS_AND_CONDITIONS.VALIDATION_MESSAGE
                  )
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Checkbox {...rest}>
          <div className="terms-text-wrapper">
            <Text className="terms-text">
              {LOCALIZATION.USER_SIGN_UP.TERMS_AND_CONDITIONS.CHECKBOX_LABEL}
            </Text>
            <Button type="link" onClick={showModal} className="terms-link">
              {LOCALIZATION.USER_SIGN_UP.TERMS_AND_CONDITIONS.LINK_TEXT}
            </Button>
          </div>
        </Checkbox>
      </Form.Item>

      <Modal
        title={LOCALIZATION.USER_SIGN_UP.TERMS_AND_CONDITIONS.MODAL_TITLE}
        open={isModalVisible}
        onCancel={handleModalClose}
        width={800}
        centered
        footer={
          <div className="modal-footer">
            <Button
              key="close"
              onClick={handleModalClose}
              size="large"
              type="default"
            >
              {LOCALIZATION.USER_SIGN_UP.TERMS_AND_CONDITIONS.CLOSE}
            </Button>
            <Button
              key="agree"
              type="primary"
              onClick={handleModalAgree}
              size="large"
            >
              {LOCALIZATION.USER_SIGN_UP.TERMS_AND_CONDITIONS.AGREE}
            </Button>
          </div>
        }
        className="terms-conditions-modal"
      >
        <div className="terms-content">
          <Title level={3}>Terms of Use – Vytelle LLC Web-Based Software</Title>
          <Paragraph>
            Welcome. You have reached this page because the web-based software
            (“Vytelle.io”) you are visiting is owned, operated, and administered
            by <Text strong>Vytelle LLC</Text> (“Vytelle”). These Terms of Use
            (“Terms”) form a binding legal agreement between you and Vytelle
            regarding your use of Vytelle.io. By choosing to access or use
            Vytelle.io, you confirm that you have carefully read, understood,
            and agreed to comply, without limitation or qualification, with
            these Terms as well as our Privacy Policy, which is expressly
            incorporated by reference. If you do not agree to these Terms, you
            are not permitted to access or use Vytelle.io.
          </Paragraph>

          <Title level={3}>
            1. Proprietary Rights in Vytelle.io and Authorized Uses
          </Title>
          <Paragraph>
            We encourage you to explore our Site and make use of the information
            it provides. However, you must understand that all elements of
            Vytelle.io—including its structure, layout, appearance, written
            content, data compilations, graphics, logos, images, videos, sound
            recordings, design components, “look and feel,” trademarks, service
            marks, and all other related materials (together, the “Vytelle
            Content”)—belong exclusively to Vytelle, its subsidiaries,
            affiliated entities, partners, or licensors, unless otherwise
            indicated. These rights are safeguarded by the intellectual property
            laws of Kansas, United States and by applicable international
            conventions and treaties.
          </Paragraph>

          <Paragraph>
            Unless specifically authorized in writing, you may not use
            Vytelle.io or any portion of the Vytelle Content for any commercial
            or public purpose. Vytelle grants you only a limited, non-exclusive,
            non-transferable license to view and access Vytelle.io for your own
            personal, non-commercial purposes. You may not copy, alter, adapt,
            republish, transmit, display, perform, distribute, or create
            derivative works from the Vytelle Content in any form without our
            prior written approval. Any proprietary notices attached to or
            contained within Vytelle.io must remain intact and unaltered.
          </Paragraph>

          <Paragraph>
            By using Vytelle.io, you acknowledge that unauthorized use of the
            Vytelle Content may violate copyright, trademark, publicity, or
            privacy laws, and could also breach applicable communications
            statutes. You further agree that Vytelle shall not be held liable
            for any improper or unlawful use of Vytelle.io by you or on your
            behalf.
          </Paragraph>

          <Title level={3}>2. Prohibited Methods of Access</Title>
          <Paragraph>
            Unless expressly authorized in writing by Vytelle, no one is
            permitted to exploit Vytelle.io in ways not outlined in these Terms.
            Without limitation, you may not resell, sublicense, republish, or
            redistribute any aspect of the Vytelle Content.
          </Paragraph>
          <Paragraph>
            Furthermore, without Vytelle’s written permission, you must not,
            directly or indirectly, use any automated software, algorithm, bot,
            scraper, spider, or manual process to:
            <ul>
              <li>
                interfere with or disrupt the normal functioning of Vytelle.io;
              </li>
              <li>
                harvest, mine, or systematically collect information from
                Vytelle.io;
              </li>
              <li>
                insert hidden code, unauthorized metadata, or concealed text;
              </li>
              <li>
                attempt to gain access to restricted areas of Vytelle’s systems
                or data;
              </li>
              <li>
                overload or place disproportionate demands on our servers or
                infrastructure; or
              </li>
              <li>
                frame, mirror, or otherwise display Vytelle.io or any portion of
                it in a way that misrepresents ownership or misuses our brand.
              </li>
            </ul>
          </Paragraph>
          <Paragraph>
            We reserve the right, at our sole discretion, to suspend or
            terminate your ability to access Vytelle.io if we determine that you
            have violated these prohibitions.
          </Paragraph>

          <Title level={3}>3. Misuse and Improper Conduct</Title>
          <Paragraph>
            You are strictly prohibited from using Vytelle.io for unlawful,
            harmful, or offensive purposes. This includes, but is not limited
            to, posting, uploading, transmitting, or otherwise making available
            any material that is: abusive, threatening, defamatory, obscene,
            pornographic, fraudulent, misleading, inflammatory, harassing,
            libelous, profane, objectionable, or otherwise inappropriate.
          </Paragraph>
          <Paragraph>
            You are also forbidden from sharing content that encourages conduct
            which could constitute a criminal offense, expose you or others to
            civil liability, or otherwise contravene Kansas, United States law
            or international intellectual property protections.
          </Paragraph>

          <Title level={3}>4. External Links and Third-Party Resources</Title>
          <Paragraph>
            The Site may contain hyperlinks that connect you to external
            websites or resources hosted on servers not controlled or maintained
            by Vytelle. These links are provided solely for your convenience.
            Vytelle does not endorse, sponsor, or assume responsibility for the
            accuracy, timeliness, or reliability of the information found on
            such third-party websites. We make no representations or warranties
            of any kind regarding those sites or their content.
          </Paragraph>
          <Paragraph>
            Accessing third-party websites linked from our Site is entirely at
            your own risk. Vytelle disclaims all responsibility and liability
            for any damages or consequences resulting from your use of, or
            reliance on, content or services obtained through linked sites.
          </Paragraph>

          <Title level={3}>5. Trademarks and Brand Features</Title>
          <Paragraph>
            The logos, service marks, trade names, trade dress, and other brand
            elements displayed on Vytelle.io (collectively, the “Trademarks”)
            are registered or unregistered marks owned by Vytelle, its
            affiliates, or its partners, unless otherwise noted. In addition,
            all page headers, custom graphics, button icons, and scripts
            appearing on Vytelle.io may constitute Trademarks of Vytelle.
          </Paragraph>
          <Paragraph>
            You acknowledge that the Trademarks remain the sole and exclusive
            property of their respective owners. Nothing contained on Vytelle.io
            should be interpreted as granting you, whether by implication,
            estoppel, or otherwise, any license or right to use any Trademark
            without the prior written consent of Vytelle or the relevant
            Trademark owner. Any misuse of the Trademarks, whether through
            copying, alteration, or unauthorized display, is strictly forbidden
            and may result in enforcement action.
          </Paragraph>

          <Title level={3}>
            6. Copyright Protection and Software Restrictions
          </Title>
          <Paragraph>
            All materials and works of authorship on Vytelle.io (the “Works”)
            are subject to copyright protection under the laws of Kansas, United
            States and applicable international treaties. The Works include,
            without limitation, all written materials, audiovisual content,
            compilations, data structures, and other creative elements that make
            up the Vytelle Content.
          </Paragraph>
          <Paragraph>
            Except for the limited license granted in these Terms, no other
            rights are conveyed. You may not reproduce, distribute, modify,
            publish, transmit, publicly display, publicly perform, or create
            derivative works from the Works without prior written authorization
            from Vytelle LLC. Additionally, you may not decompile, disassemble,
            reverse engineer, or attempt to discover the source code, design
            logic, or algorithms that underpin Vytelle.io, unless such
            restriction is expressly prohibited by law.
          </Paragraph>
          <Paragraph>
            Vytelle takes the protection of its own copyrights, as well as the
            rights of others, very seriously. If you believe that your
            copyrighted work is being used on Vytelle.io without permission,
            please refer to Section 12 for instructions on how to file a notice.
          </Paragraph>

          <Title level={3}>7. Privacy Practices and Data Usage</Title>
          <Paragraph>
            Vytelle values and respects your privacy. Our Privacy Policy, which
            forms part of these Terms, explains the information we collect, how
            we use it, and how we protect it. By accessing or using Vytelle.io,
            you consent to Vytelle’s collection, storage, and processing of your
            personal information as described in the Privacy Policy.
          </Paragraph>
          <Paragraph>
            We may, at our discretion, use information we collect to customize
            promotional content, marketing campaigns, and user experiences, as
            further detailed in the Privacy Policy.
          </Paragraph>

          <Title level={3}>8. Account Creation and Responsibilities</Title>
          <Paragraph>
            Certain features of Vytelle.io may allow you to register an account.
            If you choose to do so, you agree to:
            <ul>
              <li>
                provide accurate, current, and complete registration details;
              </li>
              <li>
                maintain the confidentiality of your login credentials; and
              </li>
              <li>
                accept responsibility for all activities conducted under your
                account.
              </li>
            </ul>
          </Paragraph>
          <Paragraph>
            You are not permitted to register an account or submit information
            on behalf of another person without proper authorization.
          </Paragraph>

          <Title level={3}>9. Submissions and User Contributions</Title>
          <Paragraph>
            If you are of legal age in your jurisdiction (or otherwise legally
            permitted), you may submit suggestions, feedback, data, images,
            text, or other materials (“Submissions”) through Vytelle.io.
          </Paragraph>
          <Paragraph>
            By submitting content, you grant Vytelle, its affiliates, employees,
            and partners an irrevocable, worldwide, perpetual, royalty-free,
            fully sublicensable license to use, reproduce, modify, distribute,
            adapt, translate, publicly display, and create derivative works from
            your Submissions in any medium, now known or later developed. This
            license includes the right to use your name, likeness, age, or other
            identifying details provided with your Submission.
          </Paragraph>
          <Paragraph>
            You represent and warrant that:
            <ul>
              <li>you hold all rights necessary to grant this license;</li>
              <li>your Submissions do not infringe third-party rights;</li>
              <li>
                your Submissions are lawful, virus-free, and non-defamatory; and
              </li>
              <li>
                you waive any moral rights to the fullest extent permitted by
                law.
              </li>
            </ul>
          </Paragraph>
          <Paragraph>
            You agree that Vytelle and its partners are free to use ideas,
            concepts, and know-how contained in your Submissions for any purpose
            without compensation or obligation to you.
          </Paragraph>

          <Title level={3}>10. Confidentiality of User Submissions</Title>
          <Paragraph>
            Except where otherwise specified in the Privacy Policy or related
            agreements, any Submissions you provide through Vytelle.io will be
            considered non-confidential. Vytelle assumes no obligation to treat
            such Submissions as confidential and reserves the right to disclose
            them to law enforcement or regulatory authorities when required.
          </Paragraph>

          <Title level={3}>11. Site Monitoring and Content Removal</Title>
          <Paragraph>
            Vytelle may, but is not obligated to, monitor Vytelle.io for
            inappropriate or unlawful content. We reserve the right to block,
            review, or remove any Submissions at our discretion. However, the
            responsibility for your Submissions rests solely with you. Vytelle
            is not liable for errors, omissions, defamation, obscenity, or other
            unlawful material posted by users.
          </Paragraph>
          <Paragraph>
            We are not required to host or display any Submissions and may
            refuse or remove them for any reason, including violation of these
            Terms.
          </Paragraph>

          <Title level={3}>12. Procedure for Copyright Complaints</Title>
          <Paragraph>
            Vytelle respects the intellectual property rights of others and
            expects the same from our users. If you believe that material
            available on Vytelle.io infringes your copyright, you should send us
            a written notice that includes:
            <ul>
              <li>
                identification of the copyrighted work claimed to be infringed;
              </li>
              <li>
                a description of where the alleged infringing material is
                located on Vytelle.io;
              </li>
              <li>your contact details;</li>
              <li>
                a statement that you have a good-faith belief that the use is
                unauthorized; and
              </li>
              <li>
                a statement, under penalty of perjury, that the information in
                the notice is accurate and that you are authorized to act on
                behalf of the copyright owner.
              </li>
            </ul>
          </Paragraph>
          <Paragraph>
            Upon receipt of a valid notice, we will take appropriate action,
            which may include removal of the infringing material.
          </Paragraph>

          <Title level={3}>13. Statements About Future Performance</Title>
          <Paragraph>
            From time to time, Vytelle.io may contain forward-looking statements
            regarding Vytelle’s operations, business strategies, or anticipated
            results. These statements are subject to risks and uncertainties and
            may differ materially from actual outcomes. Vytelle undertakes no
            duty to update forward-looking statements to reflect new information
            or events.
          </Paragraph>

          <Title level={3}>
            14. Site Availability and Technical Limitations
          </Title>
          <Paragraph>
            We do not guarantee that Vytelle.io will be accessible at all times.
            The Site may be unavailable due to maintenance, updates, technical
            difficulties, or other reasons beyond our control. Vytelle does not
            warrant that Vytelle.io will be compatible with all hardware,
            software, or browsers.
          </Paragraph>

          <Title level={3}>15. Disclaimers and Limits on Liability</Title>
          <Paragraph>
            You use Vytelle.io at your own risk. While Vytelle strives to keep
            information current and accurate, we make no promises regarding
            completeness, reliability, or timeliness. The Site is provided “as
            is” without warranties of any kind, express or implied, including
            but not limited to warranties of merchantability, fitness for
            purpose, and non-infringement.
          </Paragraph>
          <Paragraph>
            Vytelle and its affiliates shall not be liable for damages of any
            type, including direct, indirect, incidental, consequential, or
            punitive damages, or for loss of data, revenue, or profits. In all
            cases, Vytelle’s maximum liability to you shall not exceed one
            hundred Kansas, United States dollars ($100USD).
          </Paragraph>
          <Paragraph>
            Any claim or action related to your use of Vytelle.io must be filed
            within one (1) year from the date the cause of action arose.
          </Paragraph>

          <Title level={3}>
            16. Veterinary and Medical Information Disclaimer
          </Title>
          <Paragraph>
            The Site may include general information regarding animal health,
            treatments, or related topics. Such content is provided strictly for
            informational purposes and is not intended as medical or veterinary
            advice. Decisions concerning animal health should be made in
            consultation with a qualified veterinary professional.
          </Paragraph>

          <Title level={3}>
            17. Jurisdictional Restrictions and Compliance
          </Title>
          <Paragraph>
            Use of Vytelle.io is void where prohibited by law. Not all products
            or services mentioned on Vytelle.io may be available in every
            location. Vytelle reserves the right to restrict offerings to
            certain regions, individuals, or jurisdictions. You are responsible
            for ensuring that your use of Vytelle.io complies with the laws of
            your location.
          </Paragraph>

          <Title level={3}>18. Indemnity Obligations</Title>
          <Paragraph>
            You agree to indemnify, defend, and hold harmless Vytelle, its
            officers, directors, employees, contractors, and partners against
            all claims, damages, losses, and expenses (including attorney fees)
            arising from:
            <ul>
              <li>your breach of these Terms;</li>
              <li>your misuse of Vytelle.io; or</li>
              <li>your violation of applicable law or third-party rights.</li>
            </ul>
          </Paragraph>

          <Title level={3}>19. Entire Agreement and Changes to Terms</Title>
          <Paragraph>
            These Terms, along with the Privacy Policy and any additional
            site-specific rules, form the entire agreement between you and
            Vytelle regarding your use of Vytelle.io. This agreement supersedes
            all prior discussions, communications, or understandings.
          </Paragraph>
          <Paragraph>
            Vytelle may update these Terms at any time without notice. By
            continuing to use Vytelle.io after modifications are posted, you
            agree to be bound by the revised Terms. If a provision is found
            invalid, the remaining provisions shall remain in full effect.
          </Paragraph>

          <Title level={3}>20. Governing Law</Title>
          <Paragraph>
            These Terms, and any dispute arising from or related to them, shall
            be governed by and interpreted in accordance with the laws of
            <Text strong>Kansas, United States</Text>, without regard to
            conflict-of-laws principles.
          </Paragraph>

          <Title level={3}>
            21. Data Processing and International Transfers
          </Title>
          <Paragraph>
            The information you provide may be transferred to and processed in
            Kansas, United States or in other jurisdictions where data
            protection laws may differ from those of your country. By using
            Vytelle.io, you consent to these transfers and the handling of your
            information as described in our Privacy Policy.
          </Paragraph>

          <Title level={3}>22. Forum for Disputes</Title>
          <Paragraph>
            All legal proceedings or claims related to these Terms or Vytelle.io
            must be brought exclusively in the courts of Kansas, United States,
            which shall have sole jurisdiction over such matters.
          </Paragraph>

          <Divider />

          <Text type="secondary" style={{ fontSize: "12px" }}>
            By clicking "I Agree" below, you acknowledge that you have read,
            understood, and agree to be bound by these Terms of Use.
          </Text>
        </div>
      </Modal>
    </>
  );
}

TermsCheckbox.propTypes = {
  name: PropTypes.string,
};

export default TermsCheckbox;
